import { useId, useState, type ReactNode } from "react";
import { Badge, cn } from "@nexus/ui";
import { ChevronDown } from "lucide-react";

interface CollapsibleSectionProps {
  /** Stable anchor id — lets the form's nav rail jump to this section. */
  id?: string;
  /** 1-based step number shown in the leading badge. */
  step?: number;
  title: string;
  hint?: string;
  /** Optional count badge shown next to the title (hidden when 0/undefined). */
  count?: number;
  /** Right-aligned controls (e.g. an "Add" button) that don't toggle the section. */
  actions?: ReactNode;
  defaultOpen?: boolean;
  /** Controlled open state. When provided, the section is fully controlled. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

/**
 * One step of the vendor form. A single, quiet card — never nested inside
 * another card — whose body collapses with a height animation using the
 * `grid-template-rows: 0fr → 1fr` technique so it animates without measuring
 * content height in JS.
 */
export function CollapsibleSection({
  id,
  step,
  title,
  hint,
  count,
  actions,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  children,
}: CollapsibleSectionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const contentId = useId();

  function toggle() {
    const next = !open;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  }

  return (
    <section
      id={id}
      className="scroll-mt-6 rounded-xl border border-border/70 bg-card shadow-sm"
    >
      <div className="flex items-center gap-3 p-5">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={contentId}
          className="group flex flex-1 items-center gap-3 text-left outline-none"
        >
          {step != null && (
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold tabular-nums text-primary transition-colors group-focus-visible:ring-2 group-focus-visible:ring-ring"
            >
              {step}
            </span>
          )}
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              {title}
              {count != null && count > 0 && (
                <Badge variant="slate" className="text-[10px] tabular-nums">
                  {count}
                </Badge>
              )}
            </span>
            {hint && (
              <span className="text-xs font-normal text-muted-foreground">{hint}</span>
            )}
          </span>
        </button>

        {actions && <div className="shrink-0">{actions}</div>}

        <button
          type="button"
          onClick={toggle}
          aria-hidden
          tabIndex={-1}
          className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-300 ease-out",
              open ? "rotate-0" : "-rotate-90",
            )}
          />
        </button>
      </div>

      <div
        id={contentId}
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-out",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-4 border-t border-border/60 px-5 pb-5 pt-5">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}
